import { useMemo } from 'react';
import { useAppStore } from '../../app/hooks/useAppStore';
import { getWeakestFamiliesGlobal, getEvidenceLevelForFamily } from '../../mastery';
import { isFluencyColdStart as checkFluencyColdStart, type ModeId } from '../../modes';
import { humanizeFamilyId } from '../../content';
import type { PersonalRecords } from '../../data/dataLayer';
export interface ModeEnrichment {
  statLabel?: string;
  masteryPercent?: number;
  personalRecord?: string;
}
interface EnrichmentContext {
  fluencyCount: number;
  fluencyMastery: number;
  challengeCount: number;
  challengeMastery: number;
  weakestFamilyData: {
    familyId: string;
    masteryScore: number;
  } | null;
  /** Weten we genoeg over dat zwakste onderwerp om het bij naam te noemen? */
  weakestHasEvidence: boolean;
  overallMastery: number;
  personalRecords: PersonalRecords;
}
type ModeEnrichmentFn = (ctx: EnrichmentContext) => ModeEnrichment;

/**
 * MODE_ENRICHMENT — Per-mode strategy for the `LabStation`'s contextual
 * stats (statLabel, masteryPercent, personalRecord).
 *
 * Replaces the old switch-on-modeId pattern (F-CODE-7). Adding a new mode
 * means adding one entry here — TypeScript will surface missing modes via
 * the `Record<ModeId, ...>` constraint once `experiments` is enabled.
 */
const MODE_ENRICHMENT: Partial<Record<ModeId, ModeEnrichmentFn>> = {
  fluency: ({
    fluencyCount,
    fluencyMastery,
    personalRecords
  }) => ({
    statLabel: fluencyCount > 0 ? `${fluencyCount} onderwerpen klaar` : 'Train je snelheid',
    masteryPercent: fluencyCount > 0 ? fluencyMastery : undefined,
    personalRecord: personalRecords.fluency?.longestStreak ? `Record: ${personalRecords.fluency.longestStreak} op rij` : undefined
  }),
  // De kaart noemde het zwakste onderwerp bij naam zodra er ÉÉN antwoord op
  // stond — de selector filtert alleen op `attempts === 0`. Eén antwoord maakt
  // van een gok geen bevinding, en dit is de kaart waarop een kind leest waar
  // het volgens ons het slechtst in is. Onder de ondergrens geen naam.
  sprint: ({
    weakestFamilyData,
    weakestHasEvidence,
    personalRecords
  }) => {
    // Het record hoort bij HET ONDERWERP dat de kaart noemt. `sprint` is een map
    // per familyId; `sprint.bestScore` bestond niet en gaf altijd `undefined`,
    // waardoor deze regel nooit verscheen. Zonder onderwerp op de kaart ook geen
    // record — anders staat er een score bij iets wat er niet naast staat.
    const familyId = weakestFamilyData && weakestHasEvidence ? weakestFamilyData.familyId : undefined;
    const record = familyId ? personalRecords.sprint?.[familyId] : undefined;
    return {
      statLabel: familyId ? humanizeFamilyId(familyId) : 'Oefen je zwakste onderwerpen',
      masteryPercent: weakestFamilyData && weakestHasEvidence ? weakestFamilyData.masteryScore : undefined,
      personalRecord: record?.bestScore ? `Beste score: ${record.bestScore}%` : undefined
    };
  },
  challenge: ({
    challengeCount,
    challengeMastery,
    personalRecords
  }) => ({
    statLabel: challengeCount > 0 ? `${challengeCount} onderwerpen klaar` : 'Test je kennis',
    masteryPercent: challengeCount > 0 ? challengeMastery : undefined,
    personalRecord: personalRecords.challenge?.bestScore ? `Beste score: ${personalRecords.challenge.bestScore}%` : undefined
  }),
  free: ({
    overallMastery
  }) => ({
    statLabel: 'Alle vakken beschikbaar',
    masteryPercent: overallMastery > 0 ? overallMastery : undefined
  })
};
export interface UseLabPersonalizationResult {
  isFluencyColdStart: boolean;
  getEnrichmentForMode: (modeId: string) => ModeEnrichment;
}
export function useLabPersonalization(personalRecords: PersonalRecords): UseLabPersonalizationResult {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const groupSuffix = `_g${selectedGroup}`;
  const fluencyFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.masteryScore >= 50 && s.masteryScore < 80);
  }, [state.familyMastery, groupSuffix]);
  const fluencyCount = fluencyFamilies.length;
  const fluencyMastery = useMemo(() => {
    if (fluencyCount === 0) return 0;
    return Math.round(fluencyFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) / fluencyCount);
  }, [fluencyFamilies, fluencyCount]);
  const challengeFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.masteryScore >= 70);
  }, [state.familyMastery, groupSuffix]);
  const challengeCount = challengeFamilies.length;
  const challengeMastery = useMemo(() => {
    if (challengeCount === 0) return 0;
    return Math.round(challengeFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) / challengeCount);
  }, [challengeFamilies, challengeCount]);
  const weakestFamilyData = useMemo(() => {
    const weakest = getWeakestFamiliesGlobal(state.familyMastery || {}, selectedGroup, 1);
    return weakest.length > 0 ? weakest[0] : null;
  }, [state.familyMastery, selectedGroup]);
  const overallMastery = useMemo(() => {
    const families = Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.attempts > 0);
    if (families.length === 0) return 0;
    const sum = families.reduce((acc, [_, s]) => acc + s.masteryScore, 0);
    return Math.round(sum / families.length);
  }, [state.familyMastery, groupSuffix]);
  const weakestHasEvidence = useMemo(() => weakestFamilyData ? getEvidenceLevelForFamily(state.familyMastery, weakestFamilyData.familyId, selectedGroup) === 'sufficient' : false, [weakestFamilyData, state.familyMastery, selectedGroup]);

  // LET OP — dit is NIET hetzelfde als het bewijsniveau. `isFluencyColdStart`
  // betekent "geen enkel onderwerp in de 50-80-band", dus specifiek: er is niets
  // om op snelheid te trainen. Dat kan waar zijn bij een kind waarover we juist
  // veel weten. De twee samenvoegen zou beide betekenissen kapotmaken.
  const isFluencyColdStart = useMemo(() => checkFluencyColdStart(state.familyMastery || {}, selectedGroup), [state.familyMastery, selectedGroup]);
  const getEnrichmentForMode = (modeId: string): ModeEnrichment => {
    const fn = MODE_ENRICHMENT[modeId as ModeId];
    if (!fn) return {};
    return fn({
      fluencyCount,
      fluencyMastery,
      challengeCount,
      challengeMastery,
      weakestFamilyData,
      weakestHasEvidence,
      overallMastery,
      personalRecords
    });
  };
  return {
    isFluencyColdStart,
    getEnrichmentForMode
  };
}